import { PasswordInput } from '@inflearn/ds-react';
import type { MantineSize } from '@mantine/core';
import _ from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { Controller, type FieldErrors, type Path, useFormContext } from 'react-hook-form';

import { useState } from 'react';
import { usePasswordValidation } from '../../../../../hooks/usePasswordValidation';
import ValidationMessage from '../../../../common/ValidationMessage';
import { ValidationPopover } from '../../../../common/ValidationPopover/ValidationPopover';

export interface PasswordFormValue {
  password: string;
  passwordConfirm: string;
}

export type PasswordVerifyFormItemsProps<T> = T extends PasswordFormValue
  ? {
      errors: FieldErrors<T>;
      size?: MantineSize;
      showLabel?: boolean;
    }
  : never;

export const PasswordVerifyFormItems = <T extends PasswordFormValue>({
  errors,
  size = 'md',
  showLabel = true,
}: PasswordVerifyFormItemsProps<T>) => {
  const { t } = useTranslation();
  const { onValidate, validationMessages } = usePasswordValidation();
  const [isFocus, setFocus] = useState(false);
  const { control } = useFormContext<T>();

  return (
    <>
      <Controller
        name={'password' as Path<T>}
        control={control}
        render={({ field }) => (
          <ValidationPopover opened={isFocus} validationMessages={validationMessages}>
            <span>
              <PasswordInput
                value={field.value}
                onChange={(e) => {
                  /** 공백 제외 */
                  e.target.value = e.target.value.replace(/\s/g, '');
                  field.onChange(e);
                  onValidate(e.target.value);
                }}
                size={size}
                label={showLabel ? t('비밀번호') : undefined}
                placeholder={t('auth.password.placeholder')}
                error={!_.isUndefined(errors.password?.message)}
                onFocus={() => setFocus(true)}
                onBlur={() => {
                  setFocus(false);
                  field.onBlur();
                }}
              />
            </span>
          </ValidationPopover>
        )}
      />

      <Controller
        name={'passwordConfirm' as Path<T>}
        control={control}
        render={({ field }) => (
          <PasswordInput
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            size={size}
            label={showLabel ? t('비밀번호 확인') : undefined}
            placeholder={t('auth.password.placeholder')}
            error={
              errors.passwordConfirm?.message && (
                <ValidationMessage message={errors.passwordConfirm.message} />
              )
            }
          />
        )}
      />
    </>
  );
};
